import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DataTableDirective } from '../../../shared/directives/data-table.directive';
import { ColumnConfig } from '../../../shared/models/column-config.model';
import { AuthService } from '../../../shared/services/auth.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PageStateManagerService } from '../../../shared/services/page-state-manager.service';
import { TitleService } from '../../../shared/services/title.service';
import { Member } from '../../members/models/member.model';
import { InvoiceService } from '../invoice.service';
import { Invoice } from '../models/invoice.model';
import { MembersService } from '../../members/members.service';

@Component({
  selector: 'app-invoice-list',
  standalone: false,
  templateUrl: './invoice-list.component.html',
  styleUrl: './invoice-list.component.scss'
})
export class  InvoiceListComponent extends DataTableDirective<Invoice> implements OnInit {
  override columns: ColumnConfig[] = [];
  override modelName = 'Invoice';
  sendingIds = new Set<number>();
  resendInvoice: Invoice | null = null;

  constructor(
    private invoiceService: InvoiceService,
    private membersService: MembersService,
    fb: FormBuilder,
    route: ActivatedRoute,
    router: Router,
    titleService: TitleService,
    notificationService: NotificationService,
    pageStateManager: PageStateManagerService,
    authService: AuthService,
  ) {
    super(
      invoiceService,
      fb,
      route,
      router,
      titleService,
      notificationService,
      pageStateManager,
      authService
    );
  }

  override ngOnInit() {
    this.columns = [
      {
        name: 'id',
        label: 'Invoice Number',
        inputType: 'read-only',
      },
      {
        name: 'invoiceDate',
        label: 'Invoice Date',
        inputType: 'date',
        validators: [Validators.required],
        errorMessages: {
          required: 'Invoice date is required'
        }
      },
      {
        name: 'memberId',
        label: 'Member',
        inputType: 'select',
        displayColumn: 'fullName',
        options: [],
        validators: [Validators.required],
        errorMessages: {
          required: 'Member is required'
        }
      },
      {
        name: 'total',
        label: 'Total',
        inputType: 'currency',
      },
      {
        name: 'emailedOn',
        label: 'Emailed On',
        inputType: 'datetime-local',
      },
      {
        name: 'id',
        label: 'Details',
        inputType: 'link',
        linkRoute: ['/invoices', ':param', 'items'],
      }
    ];
    this.getOptions();
    super.ngOnInit();
  }

  getOptions(): void {
    this.membersService.getAll().subscribe(result => {
      const memberOptions = result.data.map((member) => ({
        value: member.id,
        label: `${member.firstName} ${member.lastName}`,
      }));
      const membercolumn = this.columns.find(column => column.name === 'memberId');
      if (membercolumn) {
        membercolumn.options = memberOptions;
      }
    });
  }

  protected override getColumnsForCreate(): ColumnConfig[] {
    return this.columns.filter(column =>
      column.name !== 'total' &&
      column.name !== 'emailedOn' &&
      column.inputType !== 'link' &&
      column.inputType !== 'read-only'
    );
  }

  emailInvoice(invoice: Invoice): void {
    if (invoice.emailedOn) {
      this.resendInvoice = invoice;
      return;
    }
    this.sendEmail(invoice);
  }

  confirmResend(): void {
    const invoice = this.resendInvoice;
    this.resendInvoice = null;
    if (invoice) {
      this.sendEmail(invoice);
    }
  }

  cancelResend(): void {
    this.resendInvoice = null;
  }

  get resendMessage(): string {
    const emailedOn = new DatePipe('en-GB').transform(this.resendInvoice?.emailedOn, 'dd/MM/yyyy HH:mm');
    return `Invoice ${this.resendInvoice?.id} was already emailed on ${emailedOn}. Would you like to send it again?`;
  }

  private sendEmail(invoice: Invoice): void {
    this.sendingIds.add(invoice.id);
    this.invoiceService.emailInvoice(invoice.id).subscribe({
      next: (updated) => {
        this.sendingIds.delete(invoice.id);
        invoice.emailedOn = updated.emailedOn;
        this.notificationService.success(`Invoice ${invoice.id} emailed to ${invoice.fullName}`);
      },
      error: (error: HttpErrorResponse) => {
        this.sendingIds.delete(invoice.id);
        this.notificationService.error(error.error?.message ?? `Failed to email invoice ${invoice.id}`);
      }
    });
  }

  downloadPdf(invoice: Invoice): void {
    this.invoiceService.downloadPdf(invoice.id).subscribe((response) => {
      const blob = new Blob([response.body!], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoice.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

}
